<template>
  <div class="asd20-app-template">
    <asd20-notification-group
      :notifications="activeNotificationsByType.banner"
      group-type="banner"
      @dismiss="$emit('dismiss-notification', $event)"
      @toggle-all="$emit('toggle-all')"
    />
    <asd20-app-header
      v-if="title || $slots.header"
      :title="title"
      :icon="icon"
      :version="version"
    >
      <template v-slot:default>
        <slot name="header" />
        <asd20-button
          v-if="
            !(searchable || $slots['toolbar-start'] || $slots['toolbar-end']) &&
              ($mq === 'xs' || $mq === 'sm' || $mq === 'md')
          "
          :label="sidebarTitle"
          :icon="sidebarIcon"
          size="sm"
          transparent
          horizontal
          @click.native="sidebarOpen = true"
        >
          <asd20-badge :visible="!!sidebarBadge" :value="sidebarBadge" />
        </asd20-button>
      </template>
    </asd20-app-header>
    <nav
      class="asd20-app-template__toolbar"
      v-if="searchable || $slots['toolbar-start'] || $slots['toolbar-end']"
    >
      <slot name="toolbar-start"></slot>
      <asd20-search-field
        v-if="searchable"
        :value="keywords"
        @input="$emit('update:keywords', $event)"
      />
      <asd20-button
        v-if="
          ($slots.sidebar || sidebarTitle || sidebarBadge) &&
            ($mq === 'xs' || $mq === 'sm' || $mq === 'md')
        "
        :label="sidebarTitle"
        :icon="sidebarIcon"
        size="sm"
        transparent
        horizontal
        @click.native="sidebarOpen = true"
      >
        <asd20-badge :visible="!!sidebarBadge" :value="sidebarBadge" />
      </asd20-button>
      <slot name="toolbar-end"></slot>
    </nav>

    <asd20-tab-bar
      class="asd20-app-template__tabs"
      v-if="tabs.length > 0 || $slots.tabs"
      :tabs="tabs"
    >
      <slot name="tabs" />
    </asd20-tab-bar>

    <mq-layout class="asd20-app-template__desktop-layout" mq="lg+">
      <div class="asd20-app-template__sidebar" v-if="$slots.sidebar">
        <h2 class="asd20-app-template__sidebar-title">
          <asd20-icon v-if="sidebarIcon" :name="sidebarIcon" size="sm" />
          {{ sidebarTitle }}
          <slot name="sidebar-title" />
          <asd20-badge :visible="!!sidebarBadge" :value="sidebarBadge" static />
        </h2>
        <slot name="sidebar" />
        <slot name="sidebar-footer" />
      </div>

      <div class="asd20-app-template__main">
        <asd20-notification-group
          :notifications="activeNotificationsByType.inline"
          group-type="inline"
          @dismiss="$emit('dismiss-notification', $event)"
          @toggle-all="$emit('toggle-all')"
        />
        <slot />
      </div>
    </mq-layout>

    <mq-layout :mq="['sm', 'md']" class="asd20-app-template__mobile-layout">
      <asd20-modal
        v-model="sidebarOpen"
        :title="sidebarTitle"
        :icon="sidebarIcon"
        dismissable
        windowed
      >
        <template v-slot:title>
          <slot name="sidebar-title" />
        </template>

        <template v-slot:default>
          <slot name="sidebar" />
        </template>

        <template v-slot:footer>
          <slot name="sidebar-footer" />
        </template>
      </asd20-modal>

      <div class="asd20-app-template__main">
        <slot />
      </div>
    </mq-layout>
    <asd20-notification-group
      :notifications="activeNotificationsByType.floating"
      group-type="floating"
      @dismiss="$emit('dismiss-notification', $event)"
      @toggle-all="$emit('toggle-all')"
    />
  </div>
</template>

<script>
import Asd20AppHeader from '../../organisms/Asd20AppHeader'
import Asd20Modal from '../../molecules/Asd20Modal'
import Asd20SearchField from '../../molecules/Asd20SearchField'
import Asd20TabBar from '../../organisms/Asd20TabBar'
import Asd20Badge from '../../atoms/Asd20Badge'
import Asd20NotificationGroup from '@asd20/notifications-ui/src/components/Asd20NotificationGroup'

export default {
  name: 'Asd20AppTemplate',
  components: {
    Asd20AppHeader,
    Asd20Modal,
    Asd20SearchField,
    Asd20TabBar,
    Asd20Badge,
    Asd20NotificationGroup,
  },

  props: {
    title: { type: String, default: '' },
    icon: { type: String, default: '' },
    version: { type: String, default: '' },
    sidebarBadge: { type: Number, default: 0 },
    sidebarTitle: { type: String, default: '' },
    sidebarIcon: { type: String, default: '' },
    tabs: { type: Array, default: () => [] },
    activeNotificationsByType: { type: Array, default: () => [] },
    searchable: { type: Boolean, default: false },
    keywords: { type: String, default: '' },
  },

  data: () => ({
    sidebarOpen: false,
  }),
}
</script>

<style lang="scss" scoped>
@import '../../../design/_variables.scss';
@import '../../../design/_mixins.scss';
@import '../../../design/_typography.scss';
@import '../../../design/_template.scss';
@import '../../../design/tokens.css';

.asd20-app-template {
  @include typography;
  @include template;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-grow: 1;

  &__toolbar {
    border-bottom: 1px solid var(--color__tertiary);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    .asd20-search-field {
      flex-grow: 1;
    }
    .asd20-button:not(.back-button) {
      &::v-deep .asd20-button__label {
        display: none;
      }
    }
  }

  &__main {
    position: relative;
    flex-grow: 1;
  }
}

.asd20-app-template__mobile-layout {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-grow: 1;
}

$filters-width: space(15);

@media (min-width: 1024px) {
  .asd20-app-template {
    overflow: hidden;
    flex-grow: 1;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    &__desktop-layout {
      display: block;
      position: relative;
      flex-grow: 1;
      overflow: hidden;
    }
    &__desktop-layout .asd20-app-template__main {
      display: flex;
      flex-direction: column;
      position: absolute;
      left: $filters-width;
      top: 0;
      right: 0;
      bottom: 0;
      overflow-y: auto;
      &:first-child {
        left: 0;
      }
    }

    &__desktop-layout .asd20-app-template__sidebar {
      position: absolute;
      left: 0;
      top: 0;
      right: calc(100% - #{$filters-width});
      bottom: 0;

      display: flex;
      box-sizing: border-box;
      flex-direction: column;
      overflow: hidden;
      height: 100%;
      background: white;
      // background: var(--website-page__alternate-background-t70);

      border-right: 1px solid var(--color__tertiary);
      order: 2;
    }

    &__sidebar-title {
      position: relative;
      display: flex;
      align-items: center;
      flex-shrink: 0;
      margin: 0;
      font-size: 1rem;
      min-height: space(2);
      color: var(--website-card__reverse-background-color);
      padding: 0 space(0.5);

      & > .asd20-icon {
        margin-right: space(0.5);
        padding: space(0.5) 0;
      }

      & > .asd20-badge {
        margin-left: auto;
      }

      & > .asd20-button {
        margin-left: auto;
        order: 3;
      }
    }

    &__toolbar .asd20-button__label {
      display: block;
    }
  }
}
</style>
